import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";

import React, { useEffect, useMemo, useState } from "react";
import { CreateExercise } from "./CreateExercise";
import { Exercise } from "./types";
import { ExerciseSetList } from "./ExerciseSetList";
import { useWod } from "../../context/WODContext";
import { useTranslation } from "react-i18next";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";

export const CreateSet = () => {
  const [name, setName] = useState<string>("");
  const [duration, setDuration] = useState<string>("0");
  const [durationType, setDurationType] = useState<string>("Minutes");
  const [setsType, setSetsType] = useState<string>("COUNT");
  const [reps, setReps] = useState<number>(1);

  const [showExerciseCreate, setShowExerciseCreate] = useState<boolean>(false);

  const [exercisesForSet, setExercisesForSet] = useState<Array<Exercise>>([]);

  const [currentExerciseItemToEdit, setCurrentExerciseItemToEdit] =
    useState<Exercise | null>(null);

  const {
    wodSets,
    setWodSets,
    exercises,
    currentSetIndex,
    setCurrentSetIndex,
  } = useWod();

  const searchableExercises: Array<{ label: string }> = useMemo(() => {
    return exercises.map((item) => ({ label: item.name }));
  }, [exercises]);

  const { t } = useTranslation();

  const handleDurationTypeChange = (event: SelectChangeEvent) => {
    setDurationType(event.target.value as string);
  };

  const handleSetTypeChange = (event: SelectChangeEvent) => {
    setSetsType(event.target.value as string);
  };

  const handleRepsChange = (event: SelectChangeEvent) => {
    setReps(Number(event.target.value));
  };

  const onNameChange = (event: any) => {
    setName(event.target.value);
  };

  const onDurationChange = (event: any) => {
    setDuration(event.target.value);
  };

  const onAddExerciseClicked = () => {
    setShowExerciseCreate(true);
  };

  const handleExerciseDelete = (id: number) => {
    let remainingExercises = exercisesForSet.filter((v) => v.id !== id);
    remainingExercises = remainingExercises.map((ex, i) => {
      return {
        ...ex,
        id: i,
      };
    });
    setExercisesForSet(remainingExercises);
  };

  const handleExerciseEdit = (id: number) => {
    let editableExerciseSet = exercisesForSet.find((v) => v.id === id) || null;
    setCurrentExerciseItemToEdit(editableExerciseSet);
    setShowExerciseCreate(true);
  };

  const handleSetSave = () => {
    if (exercisesForSet.length === 0) {
      alert("Please add exercises for the set.");
      return;
    }
    let durationInMillis = 0;
    if (durationType === "Minutes") {
      durationInMillis = Number(duration) * 60000;
    } else {
      durationInMillis = Number(duration) * 1000;
    }
    if (exercisesForSet.length > 0 && name.length > 0 && Number(duration) > 0) {
      let sets = wodSets.slice(0);
      sets = sets.map((ex, i) => {
        return {
          ...ex,
          id: ++i,
        };
      });
      if (currentSetIndex !== -1) {
        sets[currentSetIndex] = {
          duration: durationInMillis,
          exercises: exercisesForSet,
          id: sets.length + 1,
          name,
          repetitions: reps,
          type: setsType,
        };
      } else {
        sets.push({
          duration: durationInMillis,
          exercises: exercisesForSet,
          id: sets.length + 1,
          name,
          repetitions: reps,
          type: setsType,
        });
      }
      setCurrentSetIndex(-1);
      setWodSets(sets);
      resetUI();
    } else {
      alert("Please enter proper details to go ahead.");
    }
  };

  const handleOnExerciseEdit = () => {};

  const handleOnExerciseRemove = () => {
    setShowExerciseCreate(false);
    setCurrentExerciseItemToEdit(null);
  };

  const handleOnExerciseAdd = (exercise: Exercise) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let _exercises = exercisesForSet.slice(0);
    if (currentExerciseItemToEdit) {
      const editingItemIndex = _exercises.findIndex(
        (item) => item.id === currentExerciseItemToEdit.id
      );
      _exercises[editingItemIndex] = exercise;
      _exercises = _exercises.slice(0);
    } else {
      _exercises.push(exercise);
    }
    setExercisesForSet(_exercises);
  };

  const resetUI = () => {
    setName("");
    setDuration("0");
    setDurationType("Minutes");
    setSetsType("COUNT");
    setReps(1);
    setShowExerciseCreate(false);
    setExercisesForSet([]);
  };

  const handleReset = () => {
    resetUI();
    setCurrentExerciseItemToEdit(null);
    setCurrentSetIndex(-1);
  };

  const populateEditableSet = () => {
    const selectedSet = wodSets[currentSetIndex];
    setName(selectedSet.name);
    let selectedSetDuration = 0;
    if (selectedSet.duration >= 60000) {
      selectedSetDuration = selectedSet.duration / 60000;
      setDurationType("Minutes");
    } else {
      selectedSetDuration = selectedSet.duration / 1000;
      setDurationType("Seconds");
    }
    setDuration(selectedSetDuration.toString());
    setSetsType(selectedSet.type);
    setReps(selectedSet.repetitions);
    setExercisesForSet(selectedSet.exercises);
  };

  useEffect(() => {
    if (currentSetIndex !== -1) {
      populateEditableSet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSetIndex]);

  return (
    <Box sx={{ display: "flex", flexDirection: "row", gap: 3, flex: 1 }}>
      {/* Left: Set Form */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                {t("create_wod")}
              </Typography>
              <Button
                color="info"
                variant="outlined"
                size="small"
                onClick={handleReset}
                startIcon={<RestartAltIcon />}
                sx={{ textTransform: "none", fontWeight: 500 }}
              >
                Reset
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <TextField
              margin="dense"
              id="name"
              label="Set Name"
              onChange={onNameChange}
              value={name}
              type="text"
              fullWidth
              variant="outlined"
              size="small"
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 1.5,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Repetitions
              </Typography>
              <Select
                sx={{ width: 120, height: 40 }}
                value={reps.toString()}
                label="Reps"
                variant="outlined"
                size="small"
                onChange={handleRepsChange}
              >
                {["1", "2", "3", "4", "5"].map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
              <TextField
                margin="dense"
                id="duration"
                label="Duration"
                value={duration}
                type="number"
                onChange={onDurationChange}
                fullWidth
                variant="outlined"
                size="small"
              />
              <Select
                sx={{ width: 140, height: 40, mt: 1 }}
                value={durationType}
                variant="outlined"
                size="small"
                onChange={handleDurationTypeChange}
              >
                <MenuItem value="Minutes">Minutes</MenuItem>
                <MenuItem value="Seconds">Seconds</MenuItem>
              </Select>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 1.5,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Set Type
              </Typography>
              <Select
                sx={{ width: 120, height: 40 }}
                value={setsType}
                variant="outlined"
                size="small"
                onChange={handleSetTypeChange}
              >
                <MenuItem value="COUNT">COUNT</MenuItem>
                <MenuItem value="AMRAP">AMRAP</MenuItem>
                <MenuItem value="TIME">TIME</MenuItem>
              </Select>
            </Box>
          </CardContent>
        </Card>

        {exercisesForSet.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <ExerciseSetList
              onExerciseEditClicked={handleExerciseEdit}
              onExerciseDeleteClicked={handleExerciseDelete}
              exercises={exercisesForSet}
            />
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            mt: 2,
          }}
        >
          <Button
            color="secondary"
            variant="outlined"
            size="small"
            onClick={onAddExerciseClicked}
            startIcon={<AddIcon />}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Add Exercise
          </Button>
          <Button
            color="primary"
            variant="contained"
            size="small"
            onClick={handleSetSave}
            startIcon={<SaveIcon />}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
          >
            {currentSetIndex !== -1 ? "Update Set" : "Save Set"}
          </Button>
        </Box>
      </Box>

      {/* Right: Exercise Form */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {showExerciseCreate && (
          <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <CreateExercise
                id={
                  currentExerciseItemToEdit
                    ? currentExerciseItemToEdit.id
                    : exercisesForSet.length
                }
                onExerciseAdd={handleOnExerciseAdd}
                onExerciseRemove={handleOnExerciseRemove}
                onExerciseEdit={handleOnExerciseEdit}
                setType={setsType}
                isEditing={currentExerciseItemToEdit !== null}
                exercises={searchableExercises}
                currentExerciseItemToEdit={currentExerciseItemToEdit}
              />
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};
